<script>
import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import PageHeader from "@/components/page-header";
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import Swal from "sweetalert2";

export default {
    page: {
        title: "Season Color Management",
        meta: [{ name: "description", content: appConfig.description }],
    },
    components: { Layout, PageHeader },
    data() {
        return {
            title: "Season Color Management",
            loading: false,
            saving: false,
            selectedYear: null,
            newYear: "",
            editYearModal: false,
            editYearTarget: null,
            editYearValue: "",
            form: {
                early_bird: "#6F42C1",
                local: "#28A745",
                national: "#FFC107",
                late_born: "#ADB5BD",
                genesis: "#0DCAF0",
            },
            defaultColors: {
                early_bird: "#6F42C1",
                local: "#28A745",
                national: "#FFC107",
                late_born: "#ADB5BD",
                genesis: "#0DCAF0",
            },
            seasons: [
                { key: "early_bird", label: "Early Bird" },
                { key: "local", label: "Local" },
                { key: "national", label: "National" },
                { key: "late_born", label: "Late Born" },
                { key: "genesis", label: "Genesis" },
            ],
        };
    },
    computed: {
        ...mapGetters("seasonColors", ["allConfigs", "logs"]),
    },
    methods: {
        ...mapActions("seasonColors", {
            fetchAll: "fetchAll",
            saveColors: "save",
            activateColors: "activate",
            updateYearAction: "updateYear",
            fetchActive: "fetchActive",
        }),
        async loadData() {
            this.loading = true;
            await this.fetchAll();
            this.loading = false;
            if (this.allConfigs.length > 0 && !this.selectedYear) {
                this.selectYear(this.allConfigs[0].year);
            }
        },
        selectYear(year) {
            this.selectedYear = year;
            var config = this.allConfigs.find(function (c) { return c.year === year; });
            if (config) {
                this.form.early_bird = config.early_bird || this.defaultColors.early_bird;
                this.form.local = config.local || this.defaultColors.local;
                this.form.national = config.national || this.defaultColors.national;
                this.form.late_born = config.late_born || this.defaultColors.late_born;
                this.form.genesis = config.genesis || this.defaultColors.genesis;
            } else {
                this.resetForm();
            }
        },
        resetForm() {
            this.form.early_bird = this.defaultColors.early_bird;
            this.form.local = this.defaultColors.local;
            this.form.national = this.defaultColors.national;
            this.form.late_born = this.defaultColors.late_born;
            this.form.genesis = this.defaultColors.genesis;
        },
        async addNewYear() {
            var yearVal = String(this.newYear).trim();
            if (!yearVal || yearVal.length !== 4 || isNaN(yearVal)) {
                Swal.fire({ icon: "warning", title: "Invalid Year", text: "Please enter a valid 4-digit year." });
                return;
            }
            var yearNum = parseInt(yearVal);
            var exists = this.allConfigs.find(function (c) { return c.year == yearNum; });
            if (exists) {
                Swal.fire({ icon: "warning", title: "Duplicate", text: "Configuration for year " + yearNum + " already exists. Select it from the list." });
                this.selectYear(yearNum);
                this.newYear = "";
                return;
            }
            this.selectedYear = yearNum;
            this.resetForm();
            this.newYear = "";
        },
        async saveChanges() {
            if (!this.selectedYear) return;
            this.saving = true;
            var pl = {
                year: this.selectedYear,
                early_bird: this.form.early_bird,
                local: this.form.local,
                national: this.form.national,
                late_born: this.form.late_born,
                genesis: this.form.genesis,
            };
            var res = await this.saveColors(pl);
            this.saving = false;
            if (res && res.message) {
                Swal.fire({ icon: "success", title: "Saved!", text: res.message, timer: 2000, timerProgressBar: true });
                await this.fetchAll();
                await this.fetchActive();
            } else {
                Swal.fire({ icon: "error", title: "Failed", text: res && res.message ? res.message : "Could not save colors." });
            }
        },
        async activateYear(year) {
            var confirmed = await Swal.fire({
                title: "Activate Year " + year,
                text: "This will set year " + year + " as the active season color configuration across all modules.",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Activate",
                confirmButtonColor: "#34c38f",
            }).then(function(r) { return r.isConfirmed; });
            if (confirmed) {
                var res = await this.activateColors({ year: year });
                if (res && res.message) {
                    Swal.fire({ icon: "success", title: "Activated!", text: res.message, timer: 2000, timerProgressBar: true });
                    await this.fetchAll();
                    await this.fetchActive();
                } else {
                    Swal.fire({ icon: "error", title: "Failed", text: res && res.message ? res.message : "Could not activate." });
                }
            }
        },
        openEditYear(year) {
            this.editYearTarget = year;
            this.editYearValue = String(year);
            this.editYearModal = true;
        },
        async submitEditYear() {
            var newYearVal = String(this.editYearValue).trim();
            if (!newYearVal || newYearVal.length !== 4 || isNaN(newYearVal)) {
                Swal.fire({ icon: "warning", title: "Invalid Year", text: "Please enter a valid 4-digit year." });
                return;
            }
            var newYearNum = parseInt(newYearVal);
            if (newYearNum === this.editYearTarget) {
                this.editYearModal = false;
                return;
            }
            var exists = this.allConfigs.find(function(c) { return c.year == newYearNum; });
            if (exists) {
                Swal.fire({ icon: "warning", title: "Duplicate", text: "Year " + newYearNum + " already exists." });
                return;
            }
            var res = await this.updateYearAction({ old_year: this.editYearTarget, year: newYearNum });
            if (res && res.message) {
                Swal.fire({ icon: "success", title: "Updated!", text: res.message, timer: 2000, timerProgressBar: true });
                this.editYearModal = false;
                if (this.selectedYear === this.editYearTarget) {
                    this.selectedYear = newYearNum;
                }
                await this.fetchAll();
            } else {
                Swal.fire({ icon: "error", title: "Failed", text: res && res.message ? res.message : "Could not update year." });
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return "-";
            return new Date(dateStr).toLocaleString();
        },
    },
    mounted() {
        this.loadData();
    },
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" />
        <div class="row">
            <div class="col-12">
                <p class="text-muted mb-3">Manage the color coding of Seasons per Banding Year. These colors will be reflected across all modules.</p>
            </div>
        </div>

        <div class="row" v-if="loading">
            <div class="col-12 text-center py-5">
                <div class="spinner-border text-primary"></div>
                <p class="mt-2 text-muted">Loading...</p>
            </div>
        </div>

        <div class="row" v-if="!loading">
            <!-- Left Panel: Select Banding Year -->
            <div class="col-lg-4">
                <div class="card">
                    <div class="card-body">
                        <h5 class="fw-bold mb-3">1. Select Banding Year</h5>
                        <p class="text-muted small">Choose banding year to manage season colors.</p>

                        <div class="d-flex gap-2 mb-3">
                            <input type="number" class="form-control" v-model="newYear" placeholder="e.g. 2027" min="2020" max="2099">
                            <button class="btn btn-primary text-nowrap" @click="addNewYear()">
                                <i class="bx bx-plus me-1"></i>Add New Year
                            </button>
                        </div>

                        <table class="table table-sm table-bordered mb-0">
                            <thead class="bg-light">
                                <tr>
                                    <th>Banding Year</th>
                                    <th>Last Updated</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="config in allConfigs"
                                    :key="config.year"
                                    :class="{ 'table-active': selectedYear === config.year }"
                                    class="cursor-pointer"
                                    @click="selectYear(config.year)"
                                >
                                    <td class="fw-bold">
                                        {{ config.year }}
                                        <span v-if="config.is_active" class="badge bg-success ms-1">Active</span>
                                    </td>
                                    <td class="small text-muted">{{ formatDate(config.updated_at) }}</td>
                                    <td @click.stop>
                                        <div class="d-flex gap-1 align-items-center">
                                            <button
                                                class="btn btn-sm btn-outline-primary"
                                                @click="openEditYear(config.year)"
                                                title="Edit Year"
                                            ><i class="bx bx-edit-alt"></i></button>
                                            <button
                                                v-if="!config.is_active"
                                                class="btn btn-sm btn-outline-success"
                                                @click="activateYear(config.year)"
                                            >Activate</button>
                                            <span v-else class="text-success small fw-bold"><i class="bx bx-check-circle"></i> Active</span>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="allConfigs.length === 0">
                                    <td colspan="3" class="text-center text-muted py-3">No configurations yet.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Middle Panel: Configure Colors -->
            <div class="col-lg-5">
                <div class="card">
                    <div class="card-body">
                        <h5 class="fw-bold mb-3">2. Configure Season Colors for {{ selectedYear || '—' }}</h5>
                        <p class="text-muted small">Set the color for each season. These colors will be used across the system.</p>

                        <div v-if="selectedYear">
                            <div class="mb-3" v-for="season in seasons" :key="season.key">
                                <div class="d-flex align-items-center gap-3">
                                    <label class="fw-semibold" style="width: 100px;">{{ season.label }}</label>
                                    <input type="color" class="form-control form-control-color" v-model="form[season.key]" style="width: 60px; height: 38px;">
                                    <input type="text" class="form-control" v-model="form[season.key]" style="width: 100px; font-family: monospace; font-size: 13px;">
                                    <span class="badge px-3 py-2 fs-6" :style="{ backgroundColor: form[season.key], color: '#fff' }">
                                        {{ season.label }}
                                    </span>
                                </div>
                            </div>

                            <hr>
                            <div class="d-flex justify-content-end gap-2">
                                <button class="btn btn-secondary" @click="resetForm()">Reset</button>
                                <button class="btn btn-primary" :disabled="saving" @click="saveChanges()">
                                    <span v-if="saving">Saving...</span>
                                    <span v-else>Save Changes</span>
                                </button>
                            </div>
                        </div>
                        <div v-else class="text-center text-muted py-4">
                            <i class="bx bx-palette fs-1 d-block mb-2"></i>
                            Select or add a Banding Year to configure colors.
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Panel: Info -->
            <div class="col-lg-3">
                <div class="card border-info">
                    <div class="card-body">
                        <h6 class="text-info fw-bold"><i class="bx bx-info-circle me-1"></i>Information</h6>
                        <p class="small text-muted mb-2">The configured colors will automatically apply to:</p>
                        <ul class="small text-muted mb-0 ps-3">
                            <li>Dashboard</li>
                            <li>Search Entries</li>
                            <li>Seasons Module</li>
                            <li>Mobile Dashboard</li>
                            <li>Reports and Summaries</li>
                        </ul>
                    </div>
                </div>
                <div class="card border-warning">
                    <div class="card-body">
                        <h6 class="text-warning fw-bold"><i class="bx bx-error me-1"></i>Note</h6>
                        <p class="small text-muted mb-0">Only one active color configuration is allowed per Banding Year.</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Audit Logs -->
        <div class="row mt-3" v-if="!loading && logs.length > 0">
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <h5 class="fw-bold mb-3">Audit Logs (Season Color Management)</h5>
                        <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                            <table class="table table-sm table-bordered mb-0">
                                <thead class="bg-light" style="position: sticky; top: 0;">
                                    <tr>
                                        <th>Date & Time</th>
                                        <th>Banding Year</th>
                                        <th>Changes</th>
                                        <th>Updated By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(log, i) in logs" :key="i">
                                        <td class="small">{{ formatDate(log.date) }}</td>
                                        <td>{{ log.year }}</td>
                                        <td class="small">
                                            <span class="fw-semibold">{{ log.field }}:</span>
                                            <span class="text-muted">{{ log.old_value }}</span>
                                            →
                                            <span class="fw-bold" :style="{ color: log.new_value }">{{ log.new_value }}</span>
                                        </td>
                                        <td>{{ log.updated_by }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>

    <!-- Edit Year Modal -->
    <b-modal v-model="editYearModal" centered title="Edit Banding Year" title-class="font-18" hide-footer size="sm">
        <div class="mb-3">
            <label class="form-label fw-semibold">Current Year: <span class="text-muted">{{ editYearTarget }}</span></label>
            <label class="form-label fw-semibold mt-2">New Banding Year</label>
            <input
                type="number"
                class="form-control"
                v-model="editYearValue"
                placeholder="e.g. 2025"
                min="2020"
                max="2099"
            >
            <small class="text-muted mt-1 d-block">Season color configuration will be preserved.</small>
        </div>
        <div class="d-flex justify-content-end gap-2">
            <button class="btn btn-secondary" @click="editYearModal = false">Cancel</button>
            <button class="btn btn-primary" @click="submitEditYear()">Save</button>
        </div>
    </b-modal>
</template>

<style scoped>
.cursor-pointer { cursor: pointer; }
.form-control-color { padding: 2px; border: 1px solid #ced4da; }
</style>
